import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { ObjectTypes } from '@fi-shared/types';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Subject } from 'rxjs';
import { FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { ITaskV2 } from '@fi-app/task-v2/fi-task-v2';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';

interface IChecklist2ReportDeviationOneTimeTaskData {
    checkpointAnswerId: number;
    accessRightFilterCollection: IAccessRightFilterCollection;
    task: ITaskV2;
    isTaskV2: boolean;
    userId?: number;
}

@Component({
    selector: 'fi-deviation-one-time-task-editor-dialog',
    templateUrl: './deviation-one-time-task-editor-dialog.component.html',
    styleUrls: ['./deviation-one-time-task-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-deviation-one-time-task-editor-dialog' },
    standalone: false,
})
export class FiDeviationOneTimeTaskEditorDialogComponent
    extends FiDialogsContentBase<IChecklist2ReportDeviationOneTimeTaskData, ITaskV2>
    implements OnInit, OnDestroy
{
    protected task: ITaskV2;
    protected get isTaskV2() {
        return this.data.isTaskV2;
    }
    @ViewChild(FiValidatingFormDirective) taskEditorForm: FiValidatingFormDirective;
    private fiValuesComparison: FiValuesComparison<ITaskV2>;
    protected readonly ObjectTypes = ObjectTypes;

    protected get accessRightFilterCollection(): IAccessRightFilterCollection {
        return this.data.accessRightFilterCollection;
    }

    public ngOnInit(): void {
        this.setServices();
        this.getNewTaskInstance();
        this.fiValuesComparison = new FiValuesComparison(() => this.task);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    private getNewTaskInstance(): void {
        this.task = isDefined(this.data.task)
            ? cloneDeep(this.data.task)
            : this.fiTaskV2HelperService.getNewTaskV2Instance({
                  userId: this.data.userId,
                  taskType: FiTaskType.ChecklistOneTime,
                  checkpointAnswerId: this.data.checkpointAnswerId,
                  availableUserIds: this.accessRightFilterCollection.AccessRightUserFilter.UserIds,
              });
    }

    protected updateValidity(): void {
        this.taskEditorForm.updateValidity();
    }

    public isYesButtonDisabled(): boolean {
        return !!this.taskEditorForm.errors || this.taskEditorForm.pending;
    }

    public getResult(): ITaskV2 {
        return this.task;
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private fiDialogsService: FiDialogsService;
    private fiTaskV2HelperService: FiTaskV2HelperService;

    private setServices(): void {
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.fiTaskV2HelperService = this.injector.get(FiTaskV2HelperService);
    }
}
